import {
  Box,
  FormHelperText,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useCallback, useEffect, useRef, useState , CSSProperties } from "react";
import { ImageConfig } from "./FileConfig";
import { Controller, useController, useFormContext } from "react-hook-form";
import Grow from '@mui/material/Grow';
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import CLoudUploadIcon from "@mui/icons-material/CloudUploadOutlined";
import uploadImg from "../../assets/cloud-upload.png";



const CustomBox = styled(Box)({
  "&.MuiBox-root": {
    backgroundColor: "#fff",
    borderRadius: "2rem",
    boxShadow: "rgba(149, 157, 165, 0.2) 0px 8px 24px",
    padding: "1rem",
  },
  "&.MuiBox-root:hover, &.MuiBox-root.dragover": {
    opacity: 0.6,
  },
});

// Component props

interface IProps {
  limit: number;
  multiple: boolean;
  name: string;
  cssImage: CSSProperties
}

/**
 * 
 * Componer to render file upload element and set temporal url
 * with setter function 
 * 
 * @param {number} limit - limit of files
 * @param {boolean} multiple - If there are more than one files
 * @param {string} name - Name of file
 * @returns 
 */

export const Carousel: React.FC<IProps> = ({ limit, multiple, name, cssImage }) => {
  const { control, formState: { isSubmitting, errors } } = useFormContext();
  const { field } = useController({ name, control });
  const [singleFile, setSingleFile] = useState<File[]>([]);
  const [fileList, setFileList] = useState<File[]>([]);
  const wrapperRef = useRef<HTMLDivElement>(null);


  const onDragEnter = () => wrapperRef.current?.classList.add("dragover");
  const onDragLeave = () => wrapperRef.current?.classList.remove("dragover");


  const onFileDrop = useCallback(
    (e: React.SyntheticEvent<EventTarget>) => {

      const target = e.target as HTMLInputElement;
      if (!target.files) return;

      if (limit === 1) {
        const newFile = Object.values(target.files).map((file: File) => file);
        if (singleFile.length >= 1) return alert("Only a single image allowed");
        const updatedSingleList = [...singleFile, ...newFile];
        setSingleFile(updatedSingleList);
        field.onChange(updatedSingleList[0]);
      }

      if (multiple) {
        const newFiles = Object.values(target.files).map((file: File) => file);
        if (newFiles) {
          const updatedList = [...fileList, ...newFiles];
          if (updatedList.length > limit || newFiles.length > 3) {
            return alert(`Image must not be more than ${limit}`);
          }
          setFileList(updatedList);
          field.onChange(updatedList);
        }
      }
    },
    [field, fileList, limit, multiple, singleFile]
  );

  const fileRemove = (file: File) => {
    const updatedList = [...fileList];
    updatedList.splice(fileList.indexOf(file), 1);
    setFileList(updatedList);
  };

  const fileSingleRemove = (file: File) => {
    const updatedList = [...singleFile];
    updatedList.splice(singleFile.indexOf(file), 1);
    setSingleFile(updatedList);
  };

  type CustomType = "jpg" | "png" | "svg" | "webp";

  const calcSize = (size: number) => {
    return size < 1000000
      ? `${Math.floor(size / 1000)} KB`
      : `${Math.floor(size / 1000000)} MB`;
  };

  useEffect(() => {
    if (isSubmitting) {
      setFileList([]);
      setSingleFile([]);
    }
  }, [isSubmitting]);

  return (
    <>
        {((limit === 1 && singleFile.length < 1) || limit > 1 ) &&  
        <>
        <Grow in={true}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            sx={{
              position: "relative",
              width: "100%",
              height: "13rem",
              backgroundColor: "#f6f6f6",
              borderRadius: 1.5,
              mb: 3,
              mt: 2
            }}
            ref={wrapperRef}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDragLeave}
          >
            <Stack justifyContent="center" sx={{ p: 1, textAlign: "center" }}>
              <Typography sx={{ color: "#333" }}>
                {limit > 1 ? "Subir o añadir imagenes" : "Subir imagen"}
              </Typography>
              <div>
              <CLoudUploadIcon sx={{ fontSize: 60 }} color="primary"/>
              </div>
              <Typography variant="body1" component="span">
                <strong>Formatos soportados</strong>
              </Typography>
              <Typography variant="body2" component="span">
                JPG, JPEG, PNG, WEBP
              </Typography>
            </Stack>
            <Controller
              name={name}
              defaultValue=""
              control={control}
              render={({ field: { name, onBlur, ref } }) => (
                <input
                  type="file"
                  name={name}
                  onBlur={onBlur}
                  ref={ref}
                  onChange={onFileDrop}
                  multiple={multiple}
                  accept="image/jpg, image/png, image/jpeg"
                  style={{
                    opacity: 0,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    cursor: "pointer",
                  }}
                />
              )}
            />
          </Box>
        </Grow>
  
      <FormHelperText
        sx={{ textAlign: "center", my: 1 }}
        error={!!errors[name]}
      >
        {errors[name] ? (errors[name]?.message as unknown as string) : ""}
      </FormHelperText>
      </>
      }
      {fileList.length > 0 || singleFile.length > 0 ? (
        <Stack spacing={2} sx={{ my: 2 }}>
          {(multiple ? fileList : singleFile).map((item, index) => {
            const stringIndex: string = index.toString()
            return (
              <Box
                key={stringIndex + "-box1"}
                sx={{
                  position: "relative",
                  backgroundColor: "#f6f6f6",
                  borderRadius: 1.5,
                  p: 1,
                }}
              >
                <Box display="flex" key={stringIndex + "-box2"}>
                  <img
                    src={URL.createObjectURL(item)}
                    alt="upload"
                    style={cssImage}
                  />
                </Box>
                <IconButton
                  key={stringIndex + "-iconButton"}
                  onClick={() => {
                    if (multiple) {
                      fileRemove(item);
                    } else {
                      fileSingleRemove(item);
                    }
                  }}
                  sx={{
                    position: "absolute",
                    right: "1rem",
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                >
                  <DeleteIcon key={stringIndex + "-deleteIcon"} />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
      ) : null}
    </>
  );
};
